/** eslint-disable no-param-reassign */
import React, { useCallback } from 'react';
import { Box } from '@chakra-ui/core';
import isEqual from 'lodash/isEqual';
import { FormSection } from './FormSection';
import { DroppableZone } from './DroppableZone';
import { DRAG_ITEM_TYPES } from './constants';
import { DropTarget } from './types';
import { isFieldSchema, isSectionSchema } from './utils';
import { useStoreActions, useStoreState } from '../../../models/hooks';

export const FormCanvas = React.memo(() => {
  const { formSchema, draggedItem, scrollToItemId, dropIndicator } = useStoreState((state) => state.formBuilder);

  const { setScrollToItemId, addItemToCanvas, moveItemToCanvas, setDropIndicator } = useStoreActions(
    (actions) => actions.formBuilder,
  );

  const scrollableContainerRef = React.useRef<HTMLDivElement>(null);

  // Scroll to item
  React.useEffect(() => {
    if (scrollToItemId && scrollableContainerRef.current) {
      const targetElement = document.getElementById(scrollToItemId);

      if (targetElement && scrollableContainerRef.current.contains(targetElement)) {
        const canvasRect = scrollableContainerRef.current.getBoundingClientRect();
        const itemRect = targetElement.getBoundingClientRect();

        const offsetTopRelativeToCanvas = itemRect.top - canvasRect.top;

        const desiredScrollTop = scrollableContainerRef.current.scrollTop + offsetTopRelativeToCanvas - 10;

        scrollableContainerRef.current.scrollTo({
          top: desiredScrollTop,
          behavior: 'smooth',
        });
      }
      setScrollToItemId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToItemId, setScrollToItemId]);

  const handleDropOnCanvas = useCallback(
    (dropTarget: DropTarget) => {
      if (!draggedItem) return;

      const actionType = draggedItem.type === DRAG_ITEM_TYPES.PALETTE ? addItemToCanvas : moveItemToCanvas;
      actionType({ draggedItem, dropTarget });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addItemToCanvas, moveItemToCanvas, draggedItem],
  );

  const handleDragOverGlobal = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!draggedItem) {
        if (e.dataTransfer) /* eslint-disable-line */ e.dataTransfer.dropEffect = 'none';
        return;
      }
      const currentPath = [formSchema.length];
      if (!dropIndicator || isEqual(currentPath, dropIndicator.path)) {
        return;
      }

      if (e.dataTransfer) {
        const currentDropEffect: 'copy' | 'move' | 'none' = draggedItem.type === DRAG_ITEM_TYPES.PALETTE ? 'copy' : 'move';

        /* eslint-disable-line */ e.dataTransfer.dropEffect = currentDropEffect;
      }
      setDropIndicator({ path: currentPath, parentId: null, isAdvancedSectionTarget: false, show: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDropIndicator, draggedItem, formSchema.length],
  );

  const handleDropGlobal = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleDropOnCanvas({ path: [formSchema.length], parentId: null });
  };

  return (
    <Box
      ref={scrollableContainerRef}
      sx={{ flexGrow: 1, padding: 4, paddingLeft: 0, overflowY: 'auto', position: 'relative' }}
      onDragOver={handleDragOverGlobal}
      onDrop={handleDropGlobal}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.relatedTarget === null || !(e.currentTarget as Node).contains(e.relatedTarget as Node)) {
          setDropIndicator(undefined);
        }
      }}
      id="hr-form-builder-scroll-view"
    >
      <DroppableZone
        path={[0]}
        parentId={null}
        onDropItem={handleDropOnCanvas}
        isAdvancedSectionTarget={false}
        className="canvas-initial-droppable"
        hideDropZoneMessage={formSchema.length !== 0}
        sx={{ minHeight: formSchema.length === 0 ? '100%' : 4, marginLeft: 4 }}
      />

      <Box paddingLeft={4}>
        {formSchema
          ?.filter((item) => (isFieldSchema(item) ? !item?.disabled : true))
          .map((item, index) => (
            <React.Fragment key={item.id}>
              <Box>{isSectionSchema(item) && <FormSection section={item} path={[index]} onDropItem={handleDropOnCanvas} />}</Box>
              <DroppableZone
                path={[index + 1]}
                parentId={null}
                onDropItem={handleDropOnCanvas}
                isAdvancedSectionTarget={false}
                className="canvas-item-droppable"
                sx={{ paddingTop: 4 }}
              />
            </React.Fragment>
          ))}
      </Box>
    </Box>
  );
});

FormCanvas.displayName = 'FormCanvas';
